import { $, merge } from "glamor";
import { ICellRendererComp, ICellRendererParams } from "ag-grid-community";
import get from "lodash/get";
import { DEFAULT_VALUES_PINNED_ROW } from "../detail-list/helpers";
import { notePrefix } from "constants/product-collection";
import { NoteComponent } from "./note-component";

const styles = merge(
    {
        width: "100%",
        height: "100%",
    },
    $(" .focusOn", {
        position: "absolute",
        top: "-12px",
        right: "0px",
        zIndex: 2,
        color: "white",
        lineHeight: "12px",
        fontWeight: "bold",
        fontSize: "8px",
        padding: "0 2px",
        opacity: 0,
        transition: "all .2s ease-out",
    }),
    $(":hover .focusOn", {
        opacity: 0.9,
        top: "0px",
    })
);

const focusDecoratorVanilla = (Component: any) => {
    class FocusDecoratorWrapper implements ICellRendererComp {
        declare eGui: HTMLElement;
        declare eWrappedComponent;
        declare noteComponent: NoteComponent;

        constructor() {
            this.eWrappedComponent = new Component();
        }

        private buildBaseElement(params) {
            if (!this.eGui) {
                // create the cell
                this.eGui = document.createElement("div");
                this.eGui.className = `focusDecorator ${styles.toString()}`;

                // Validate if data type is different to the default row
                // The note feature support for default row will be done in the ticket NU-14670
                if (params.data?.type !== DEFAULT_VALUES_PINNED_ROW) {
                    this.noteComponent = new NoteComponent();
                    this.noteComponent.init(params);
                }
            }
        }

        private render(params: ICellRendererParams) {
            this.buildBaseElement(params);
            const { node, column, api, context } = params;
            const { getFocusOnCellMap } = context;
            let name = null;
            let color = null;
            const focusOn = getFocusOnCellMap && getFocusOnCellMap().get(`${node.id}.${column.getColId()}`);
            if (focusOn) {
                const myFocused = api.getFocusedCell();
                const myFocusedRowIndex =
                    myFocused && api.getRenderedNodes()[myFocused.rowIndex] ? myFocused.rowIndex : null;
                if (
                    !myFocused ||
                    myFocusedRowIndex !== node.rowIndex ||
                    myFocused.column.getColId() !== column.getColId()
                ) {
                    name = focusOn.getIn(["sessions", 0, "identity_info", "display_name"]);
                    color = focusOn.get("color");
                }
            }

            // Clear existing children
            while (this.eGui.lastElementChild) {
                this.eGui.removeChild(this.eGui.lastElementChild);
            }

            if (name && color) {
                const focusElement = document.createElement("div");
                focusElement.className = "focusOn";
                focusElement.innerText = name;
                focusElement.style.backgroundColor = color;
                this.eGui.appendChild(focusElement);
            }

            this.eGui.appendChild(this.eWrappedComponent.getGui());
        }

        refresh(params: ICellRendererParams): boolean {
            const { colId } = params.colDef;
            if (get(params.data, ["fields", `${notePrefix}${colId}`], {}).hasOwnProperty("string_value")) {
                return false;
            }
            this.eWrappedComponent.refresh(params);
            this.render(params);

            // return true to tell the grid we refreshed successfully
            return true;
        }
        getGui(): HTMLElement {
            return this.eGui;
        }
        destroy(): void {
            if (this.eWrappedComponent.destroy) {
                this.eWrappedComponent.destroy();
            }

            if (this.noteComponent) {
                this.noteComponent.destroy();
                delete this.noteComponent;
            }
        }
        init(params: ICellRendererParams) {
            if (this.eWrappedComponent.init) {
                this.eWrappedComponent.init(params);
            }

            this.render(params);
        }
    }

    return FocusDecoratorWrapper;
};

export { focusDecoratorVanilla };
